import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea } from "@components/Forms";
import DocumentTable from "@components/Tables/DocumentTable";
import { Breadcrumb, ConfirmDeleteModal } from "@components/UI";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, uppercase, translateDepartment } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { CHARACTER_LENGTH } from "@constants/index";

const TEST_ID = "qa-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

export default function QADetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { qnaSeq } = useParams();
  const { colors } = useTheme();
  const [qaData, setQaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [answerContent, setAnswerContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingData, setEditingData] = useState({});
  const [isUnchanged, setIsUnchanged] = useState(true);
  const [hasErrors, setHasErrors] = useState(false);

  const [documentCurrentPage, setDocumentCurrentPage] = useState(1);
  const [documentItemsPerPage] = useState(5);
  const { fileInfo, documentTotalData, getFileInformationPagination, uploadFilesAndGetOnSuccess } = useFileGroupAPI(
    qaData?.fileGroupNo || 0,
    documentCurrentPage || 1,
  );

  const getQAData = useMutationData(
    `${queryKeys.getQAById}/${qnaSeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setQaData(response);
          setEditingData(response);
        }
        setLoading(false);
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/question-and-answer" } });
        } else {
          showErrorToast(t("qa.get-qa-detail-error"));
        }
        console.error("Error during get Q&A data:", error);
        setLoading(false);
      },
    },
  );

  const increaseViewCount = useMutationData(
    `${queryKeys.increaseViewCountQA}/${qnaSeq}`,
    { method: "PUT" },
    {
      onSuccess: () => getQAData.mutateAsync(),
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/question-and-answer" } });
        }
      }
    },
  );

  const updateQA = useMutationData(
    `${queryKeys.updateQA}/${qnaSeq}`,
    { method: "PUT" },
    {
      onSuccess: async (response) => {
        if (typeof response !== "string") {
          await updateDataInIndexedDb(STORES.QNAS, response, response.qnaSeq);
          showSuccessToast(t("qa.update-qa-successfully"));
          getQAData.mutate();
          setIsEditMode(false);
          setIsUnchanged(true);
        }
      },
      onError: (error) => {
        console.error("Error updating Q&A:", error);
        showErrorToast(t("qa.update-qa-failed"));
      },
    },
  );

  const submitAnswer = useMutationData(
    `${queryKeys.updateAnswer}/${qnaSeq}`,
    { method: "PUT" },
    {
      onSuccess: (response) => {
        showSuccessToast(t("qa.answer-submitted-successfully"));
        setShowAnswerModal(false);
        setAnswerContent("");
        // Refresh data to show the new answer
        getQAData.mutateAsync();
        setIsSubmitting(false);
      },
      onError: (error) => {
        console.error("Error submitting answer:", error);
        showErrorToast(t("qa.answer-submission-failed"));
        setIsSubmitting(false);
      },
    },
  );

  // API call for deleting files
  const deleteFile = useMutationData(
    (fileInfoSeq) => `file_info/${fileInfoSeq}`,
    {
      method: "DELETE",
    },
    {
      onSuccess: () => {
        showSuccessToast(t("project.document.delete-success"));
        // Refresh file list after deletion
        if (qaData.fileGroupNo && qaData.fileGroupNo !== 0) {
          getFileInformationPagination.mutate();
          // Calculate new maximum number of pages
          const maxPages = Math.ceil((documentTotalData - 1) / documentItemsPerPage);
          // If current page exceeds maxPages, switch to the last valid page
          if (documentCurrentPage > maxPages && maxPages > 0) {
            setDocumentCurrentPage(maxPages);
          } else if (documentTotalData <= 1) {
            // If no files remain, reset to page 1
            setDocumentCurrentPage(1);
          }
        }
      },
      onError: (error) => {
        console.error("Error during delete file:", error);
        showErrorToast(t("project.document.delete-error"));
      },
    },
  );

  useEffect(() => {
    if (qnaSeq) {
      increaseViewCount.mutateAsync();
    }
  }, [qnaSeq]);

  useEffect(() => {
    if (qaData?.fileGroupNo && qaData.fileGroupNo !== 0) {
      getFileInformationPagination.mutate();
    }
  }, [qaData?.fileGroupNo, documentCurrentPage]);

  useEffect(() => {
    // Calculate the maximum number of pages based on documentTotalData
    const maxPages = Math.ceil((documentTotalData || 0) / documentItemsPerPage);

    // If documentCurrentPage exceeds maxPages, switch to the last valid page
    if (documentCurrentPage > maxPages && maxPages > 0) {
      setDocumentCurrentPage(maxPages);
    } else if (documentTotalData === 0) {
      // If there are no files left, reset to page 1
      setDocumentCurrentPage(1);
    }
  }, [documentTotalData, documentItemsPerPage, documentCurrentPage]);

  // Validation
  useEffect(() => {
    const hasRequiredFields = editingData.title?.trim() && editingData.question?.trim();
    const dataChanged = JSON.stringify(editingData) !== JSON.stringify(qaData);

    setHasErrors(!hasRequiredFields);
    setIsUnchanged(!dataChanged);
  }, [editingData, qaData]);

  // Check if save button should be enabled
  const canSave = editingData.title?.trim() && editingData.question?.trim() && !isUnchanged;

  const handleEdit = () => {
    setIsEditMode(true);
    setEditingData({ ...qaData });
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditingData({ ...qaData });
  };

  const handleSave = () => {
    if (!hasErrors && !isUnchanged) {
      updateQA.mutate(editingData);
    }
  };

  const handleInputChange = (field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAnswer = () => {
    setShowAnswerModal(true);
    setAnswerContent("");
  };

  const handleSubmitAnswer = () => {
    if (!answerContent.trim()) {
      showErrorToast(t("qa.answer-required"));
      return;
    }

    setIsSubmitting(true);
    const requestBody = {
      qnaSeq: qnaSeq,
      answer: answerContent.trim(),
    };

    submitAnswer.mutate(requestBody);
  };

  const handleCancelAnswer = () => {
    setShowAnswerModal(false);
    setAnswerContent("");
  };

  // File management functions
  const handleUploadFile = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = "*/*";
    fileInput.onchange = (event) => {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        if (!qnaSeq) {
          showErrorToast("Missing Q&A sequence.");
          return;
        }

        const uploadFilesData = files.reduce((acc, file, index) => {
          if (file instanceof File) {
            acc[`file_${index}`] = file;
          }
          return acc;
        }, {});

        const formData = new FormData();
        formData.append("targetSeq", qnaSeq);
        formData.append("targetTypeCd", "QNA");
        formData.append("fileGroupNo", qaData.fileGroupNo);

        Object.entries(uploadFilesData).forEach(([key, file]) => {
          formData.append(key, file, file.name);
        });

        uploadFilesAndGetOnSuccess.mutate(formData, {
          onSuccess: () => {
            // Refresh announcementData to update fileGroupNo
            getQAData.mutateAsync().then(() => {
              // Refresh file list with the current page
              getFileInformationPagination.mutate();
              // Calculate new number of pages based on updated documentTotalData
              const maxPages = Math.ceil((documentTotalData + files.length) / documentItemsPerPage);
              // Only update documentCurrentPage if the current page is invalid
              if (documentCurrentPage > maxPages && maxPages > 0) {
                setDocumentCurrentPage(maxPages);
              } else if (documentTotalData === 0 && files.length > 0) {
                setDocumentCurrentPage(1); // Reset to page 1 if there were no files before
              }
              // Otherwise, do not change documentCurrentPage
            });
          },
          onError: (error) => {
            console.error("Error during upload:", error);
            showErrorToast(t("common.files-upload-error"));
          },
        });
      } else {
        showErrorToast("No files selected");
      }
    };

    fileInput.click();
  };

  const handleDeleteDocument = (documentToDelete) => {
    setDocumentToDelete(documentToDelete);
    setShowDeleteDocumentModal(true);
  };

  const handleConfirmDeleteDocument = () => {
    if (!documentToDelete) return;
    setDeleteDocumentLoading(true);

    deleteFile.mutate(documentToDelete.fileInfoSeq, {
      onSettled: () => {
        setDeleteDocumentLoading(false);
        setShowDeleteDocumentModal(false);
        setDocumentToDelete(null);
      },
    });
  };

  // Document delete modal state
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div className={`${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header */}
      <div className="mb-6" data-testid={tid("header")}>
        {/* Breadcrumb */}
        <Breadcrumb
          data-testid={tid("breadcrumb")}
          items={[
            { label: t("navbar.question-and-answer-list"), href: "/question-and-answer" },
            { label: t("navbar.question-and-answer-details"), href: null },
            ...(isEditMode ? [{ label: t("qa.edit-qa") }] : []),
          ]}
          className="mb-4"
        />

        {/* Title and Action Buttons */}
        <div className="flex items-center justify-between">
          <h1
            className={`text-2xl font-bold ${colors.textPrimary} truncate`}
            data-testid={tid("page-title")}
          >
            {qaData.title}
          </h1>

          <div className="flex items-center space-x-3" data-testid={tid("header-actions")}>
            <PrimaryButton
              data-testid={tid("answer-button")}
              onClick={handleAnswer}
              className="mb-2"
            >
              {qaData?.answer ? t("qa.edit-answer") : t("qa.answer")}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Information Cards */}
      <div
        className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary}`}
        data-testid={tid("information-card")}
      >
        <div className="flex flex-col lg:flex-row">
          {/* Question Information Card */}
          <div className={`w-full p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h2
                className={`text-lg font-bold ${colors.textPrimary}`}
                data-testid={tid("information-title")}
              >
                {uppercase(t("qa.question-information"))}
              </h2>

              {/* Edit Information Button - inside the card */}
              {!isEditMode ? (
                <OutlineButton data-testid={tid("edit-button")} onClick={handleEdit}>
                  {t("qa.edit-information")}
                </OutlineButton>
              ) : (
                <div className="flex items-center space-x-3" data-testid={tid("edit-actions")}>
                  <OutlineButton data-testid={tid("cancel-button")} onClick={handleCancel}>
                    {t("common.cancel-button")}
                  </OutlineButton>
                  <PrimaryButton data-testid={tid("save-button")} onClick={handleSave} disabled={!canSave}>
                    {t("common.save-button")}
                  </PrimaryButton>
                </div>
              )}
            </div>

            {/* Title and Question Content */}
            {!isEditMode ? (
              <div className={`mt-6 mb-4`} data-testid={tid("content-view")}>
                {/* Title Field */}
                <div className="mb-6" data-testid={fieldContainerTestId("title")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("title")}>
                    {t("qa.title")}
                  </span>
                  <span
                    className={`text-sm font-medium ${colors.textMessage} break-words break-all`}
                    data-testid={fieldValueTestId("title")}
                  >
                    {qaData?.title || "---"}
                  </span>
                </div>

                {/* Question Field */}
                <div className="mb-6" data-testid={fieldContainerTestId("question")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("question")}>
                    {t("qa.question")}
                  </label>
                  <CustomTextarea
                    data-testid={fieldValueTestId("question")}
                    rows={6}
                    value={qaData?.question || ""}
                    title={qaData?.question}
                    readOnly={true}
                  />
                </div>
              </div>
            ) : (
              <div className={`pt-4`} data-testid={tid("form")}>
                <div data-testid={fieldContainerTestId("title")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("title")}>
                    {t("qa.title")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomInput
                    data-testid={fieldInputTestId("title")}
                    type="text"
                    placeholder={t("qa.title-placeholder")}
                    value={editingData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    title={editingData.title}
                    maxLength={CHARACTER_LENGTH.COMMENT}
                  />
                </div>
                <div className="md:col-span-2 mt-6" data-testid={fieldContainerTestId("question")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("question")}>
                    {t("qa.question")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomTextarea
                    data-testid={fieldInputTestId("question")}
                    rows={6}
                    placeholder={t("qa.question-placeholder")}
                    value={editingData.question}
                    onChange={(e) => handleInputChange("question", e.target.value)}
                    title={editingData.question}
                    maxLength={CHARACTER_LENGTH.CONTENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="qa-detail-question-textarea-char-count">{(editingData.question || "").length}/{CHARACTER_LENGTH.CONTENT}</p>
                </div>
              </div>
            )}

            {/* Metadata Grid */}
            <div className={`space-y-0`} data-testid={tid("metadata-section")}>
              {/* Row 1: 5 fields in one line */}
              <div className={`grid grid-cols-5 gap-8 py-4 ${colors.borderPrimary}`}>
                {/* <div data-testid={fieldContainerTestId("department-only")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("department-only")}>
                    {t("qa.department-only")}
                  </span>
                  {isEditMode ? (
                    <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                      <input
                        data-testid={fieldInputTestId("department-only")}
                        type="checkbox"
                        className="sr-only peer"
                        checked={editingData.private}
                        onChange={(e) => handleInputChange("private", e.target.checked)}
                      />
                      <div
                        className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${editingData.private ? "border-none" : colors.borderPrimary} ${editingData.private ? colors.bgActiveToggle : colors.bgInput}`}
                      ></div>
                      <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                        {editingData.private ? t("common.yes") : t("common.no")}
                      </span>
                    </label>
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid={fieldValueTestId("department-only")}
                    >
                      {qaData?.private ? t("common.yes") : t("common.no")}
                    </span>
                  )}
                </div>

                <div data-testid={fieldContainerTestId("disclose")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("disclose")}>
                    {t("qa.disclose")}
                  </span>
                  {isEditMode ? (
                    <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                      <input
                        data-testid={fieldInputTestId("disclose")}
                        type="checkbox"
                        className="sr-only peer"
                        checked={editingData.openFlag === "Y"}
                        onChange={(e) => handleInputChange("openFlag", e.target.checked ? "Y" : "N")}
                      />
                      <div
                        className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${editingData.openFlag !== "Y" ? colors.borderPrimary : "border-none"} ${editingData.openFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                      ></div>
                      <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                        {editingData.openFlag === "Y" ? t("common.yes") : t("common.no")}
                      </span>
                    </label>
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid={fieldValueTestId("disclose")}
                    >
                      {qaData?.openFlag === "Y" ? t("common.yes") : t("common.no")}
                    </span>
                  )}
                </div> */}

                <div data-testid="qa-detail-view-count-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("qa.view-count")}
                  </span>
                  <span
                    className={`text-sm font-medium ${colors.textMessage}`}
                    data-testid="qa-detail-view-count-value"
                  >
                    {qaData?.viewCnt || "0"}
                  </span>
                </div>

                {/* <div data-testid="qa-detail-creator-department-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("qa.creator-department")}
                  </span>
                  <span
                    className={`text-sm font-medium ${colors.textMessage}`}
                    data-testid="qa-detail-creator-department-value"
                  >
                    {translateDepartment(t, qaData?.createrDeptCdName) || "---"}
                  </span>
                </div> */}

                <div className="min-w-0" data-testid="qa-detail-creator-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("qa.creator")}
                  </span>
                  <UserNameWithId
                    name={qaData?.createrName}
                    id={qaData?.createrId}
                    layout="inline"
                    data-testid="qa-detail-creator-value"
                  />
                </div>

                <div data-testid="qa-detail-creation-date-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("qa.creation-date")}
                  </span>
                  <span
                    className={`text-sm font-medium ${colors.textMessage}`}
                    data-testid="qa-detail-creation-date-value"
                  >
                    {formatDateTime(qaData?.createdAt) || "---"}
                  </span>
                </div>

                <div className="min-w-0" data-testid="qa-detail-last-editor-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-by")}
                  </span>
                  <UserNameWithId
                    name={qaData?.updaterName}
                    id={qaData?.updaterId}
                    layout="inline"
                    data-testid="qa-detail-last-editor-value"
                  />
                </div>

                <div data-testid="qa-detail-last-modified-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-date")}
                  </span>
                  <span
                    className={`text-sm font-medium ${colors.textMessage}`}
                    data-testid="qa-detail-last-modified-value"
                  >
                    {formatDateTime(qaData?.updatedAt) || "---"}
                  </span>
                </div>
              </div>

              {/* Row 2: 5 fields in one line */}
              {/* <div className={`grid grid-cols-5 gap-8 pt-4`}>
                <div data-testid="qa-detail-creation-date-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("qa.creation-date")}
                  </span>
                  <span
                    className={`text-sm font-medium ${colors.textMessage}`}
                    data-testid="qa-detail-creation-date-value"
                  >
                    {formatDateTime(qaData?.createdAt) || "---"}
                  </span>
                </div>

                <div data-testid="qa-detail-last-editor-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-by")}
                  </span>
                  <span
                    className={`text-sm font-medium ${colors.textMessage}`}
                    data-testid="qa-detail-last-editor-value"
                  >
                    {qaData?.updaterName || "system"}
                  </span>
                </div>

                <div data-testid="qa-detail-last-modified-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-date")}
                  </span>
                  <span
                    className={`text-sm font-medium ${colors.textMessage}`}
                    data-testid="qa-detail-last-modified-value"
                  >
                    {formatDateTime(qaData?.updatedAt) || "---"}
                  </span>
                </div>

                <div></div>
                <div></div>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Answer Information Section - Only show if answer exists */}
      {qaData?.answer && (
        <div
          className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary} mt-6`}
          data-testid="qa-detail-answer-section"
        >
          <div className="flex flex-col lg:flex-row">
            <div className={`w-full p-6`}>
              <h2
                className={`text-lg font-bold ${colors.textPrimary}`}
                data-testid="qa-detail-answer-information-title"
              >
                {uppercase(t("bulletin.answer-information-title"))}
              </h2>

              {/* Answer Content */}
              <div
                className={`mt-6 p-6 mb-4 rounded-2xl border ${colors.bgSecondary} ${colors.borderPrimary}`}
                data-testid="qa-detail-answer-content-view"
              >
                <p
                  className={`text-sm font-normal ${colors.textMessage} break-words break-all`}
                  data-testid="qa-detail-answer-text"
                >
                  {qaData?.answer || ""}
                </p>
              </div>

              {/* Answer Metadata Grid */}
              <div className={`space-y-0`} data-testid="qa-detail-answer-metadata-section">
                <div className={`grid grid-cols-4 gap-8 py-4 border-b border-dashed ${colors.borderPrimary}`}>
                  <div data-testid="qa-detail-answerer-field">
                    <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                      {t("qa.answerer")}
                    </span>
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid="qa-detail-answerer-value"
                    >
                      {qaData?.answererName || "---"}
                    </span>
                  </div>

                  <div data-testid="qa-detail-answer-date-field">
                    <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                      {t("qa.answer-date")}
                    </span>
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid="qa-detail-answer-date-value"
                    >
                      {formatDateTime(qaData?.answeredAt) || "---"}
                    </span>
                  </div>

                  <div data-testid="qa-detail-answer-last-editor-field">
                    <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                      {t("common.last-modified-by")}
                    </span>
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid="qa-detail-answer-last-editor-value"
                    >
                      {qaData?.answererName || "---"}
                    </span>
                  </div>

                  <div data-testid="qa-detail-answer-last-modified-field">
                    <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                      {t("common.last-modified-date")}
                    </span>
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid="qa-detail-answer-last-modified-value"
                    >
                      {formatDateTime(qaData?.updatedAt) || "---"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Information Section */}
      <div
        className={`rounded-3xl border ${colors.borderPrimary} ${colors.bgPrimary} px-6 pt-6 pb-2 mt-6`}
        data-testid="qa-detail-attachments-section"
      >
        <div className="flex items-center justify-between mb-2">
          <h2
            className={`font-medium ${colors.textPrimary}`}
            data-testid="qa-detail-attachments-title"
          >
            {uppercase(t("bulletin.attachment"))} ({documentTotalData || 0})
          </h2>
          <OutlineButton data-testid="qa-detail-upload-file-button" onClick={handleUploadFile}>
            {t("project.document.upload-file")}
          </OutlineButton>
        </div>

        {fileInfo && fileInfo.length > 0 ? (
          <div data-testid="qa-detail-documents-table">
            <DocumentTable
              data={fileInfo}
              onDelete={handleDeleteDocument}
              currentPage={documentCurrentPage}
              totalPages={Math.ceil((documentTotalData || 0) / documentItemsPerPage)}
              onPageChange={setDocumentCurrentPage}
              totalData={documentTotalData}
            />
          </div>
        ) : (
          <div
            className={`py-8 text-center ${colors.textSecondary}`}
            data-testid="qa-detail-no-documents"
          >
            <p>{t("project.no-documents-found")}</p>
          </div>
        )}
      </div>

      {/* Answer Modal */}
      {showAnswerModal && (
        <div
          className={`fixed inset-0 ${colors.bgBlurModal} flex items-center justify-center z-50`}
          data-testid="qa-detail-answer-modal"
        >
          <div
            className={`${colors.bgPrimary} rounded-lg p-6 w-full max-w-2xl mx-4`}
            data-testid="qa-detail-answer-modal-content"
          >
            <h2
              className={`text-xl font-bold mb-4 ${colors.textPrimary}`}
              data-testid="qa-detail-answer-modal-title"
            >
              {t("qa.answer-the-question")}
            </h2>

            <div className="mb-6" data-testid="qa-detail-answer-modal-message-field">
              <label className={`block text-sm font-medium mb-2 ${colors.textSecondary}`}>
                {t("qa.message")} <span className={colors.textRequired}>*</span>
              </label>
              <CustomTextarea
                data-testid="qa-detail-answer-modal-textarea"
                value={answerContent || qaData?.answer || ""}
                onChange={(e) => setAnswerContent(e.target.value)}
                rows={8}
                maxLength={CHARACTER_LENGTH.CONTENT}
                className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
              />
              <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="qa-detail-answer-modal-textarea-char-count">
                {(answerContent || qaData?.answer || "").length}/{CHARACTER_LENGTH.CONTENT}
              </p>
            </div>

            <div className="flex justify-end gap-3" data-testid="qa-detail-answer-modal-actions">
              <OutlineButton data-testid="qa-detail-answer-modal-cancel-button" onClick={handleCancelAnswer}>
                {t("common.cancel-button")}
              </OutlineButton>
              <PrimaryButton
                data-testid="qa-detail-answer-modal-save-button"
                onClick={handleSubmitAnswer}
                disabled={!answerContent.trim() || isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center" data-testid="qa-detail-answer-modal-saving">
                    <div className={`mr-2 h-4 w-4 animate-spin rounded-full border-b-2 ${colors.borderOnPrimary}`} />
                    {t("common.saving")}
                  </div>
                ) : (
                  t("common.save-button")
                )}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Document Modal */}
      <ConfirmDeleteModal
        data-testid="qa-detail-delete-document-modal"
        isOpen={showDeleteDocumentModal}
        onClose={() => setShowDeleteDocumentModal(false)}
        onConfirm={handleConfirmDeleteDocument}
        title={t("bulletin.delete-document")}
        description={t("bulletin.delete-document-description")}
        loading={deleteDocumentLoading}
      />
      <div data-testid="qa-detail-upload-loader">
        <FullScreenDotLoader
          loading={uploadFilesAndGetOnSuccess.isPending}
          text={
            uploadFilesAndGetOnSuccess.isPending
              ? t("bulletin.uploading-qna") || "Uploading Q&A file..."
              : t("bulletin.upload-qna-file") || "Uploading Q&A file..."
          }
          size={80}
          speedMultiplier={1.2}
        />
      </div>
    </div>
  );
}
